import { CircularProgress } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import React, { useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { ReactComponent as RechargeIcon } from '@/assets/icons/recharge.svg';
import { PrimaryButton, Space } from '@/components';
import { useFirstRenderEffect } from '@/hooks';
import { MainLayout } from '@/layout';
import { makeSelectIsFrameView, makeSelectIsMobileView } from '@/model/global/selectors';
import {
  reloadBalance,
  reloadTransactions,
  requestTransactions,
  storeTransactionsFilter,
} from '@/model/wallets/actions';
import {
  makeSelectSelectedWalletsBalance,
  makeSelectTransactions,
  makeSelectTransactionsFilter,
} from '@/model/wallets/selectors';
import { AnyTransactionPeriod, Transaction } from '@/model/wallets/types';
import WalletValue from '@/pages/wallets/components/WalletValue';
import { asType } from '@/utils/ts';

import { TransactionFilter, TransactionRow, TransactionsDate } from './components';

const useStyles = makeStyles((theme: Theme) => ({
  bodyBox: {
    paddingTop: theme.spacing(3),
  },
  header: {
    backgroundColor: theme.colors.lightBackground,
  },
  headerMobile: {
    backgroundColor: theme.colors.primary,
  },
}));

interface DataToRender {
  tx?: Transaction;
  date?: Date;
}

const selectBalance = makeSelectSelectedWalletsBalance();
const selectIsMobile = makeSelectIsMobileView();
const selectIsFrame = makeSelectIsFrameView();
const selectFilter = makeSelectTransactionsFilter();

const mainContainerId = 'scrollableDiv';

export interface TransactionsPageViewProps {
  'data-test'?: string;
  back?: React.FC;
  basePath: string;
  withRecharge?: boolean;
  scrollToTx?: string;
  hideBalance?: boolean;
}

export const TransactionsPageView: React.FC<TransactionsPageViewProps> = ({
  back,
  basePath,
  scrollToTx,
  withRecharge,
  hideBalance,
  'data-test': dataTest,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const filter = useSelector(selectFilter);
  const filterVisible = useRef(false);
  const setFilterVisible = useCallback((isVisible: boolean) => {
    filterVisible.current = isVisible;
  }, []);
  const updateFilter = useCallback(
    async (newFilter: AnyTransactionPeriod) => {
      if (!isEqual(filter, newFilter)) {
        await storeTransactionsFilter(newFilter)(dispatch);
        await reloadTransactions({ force: true })(dispatch);
      }
    },
    [dispatch, filter],
  );

  const loading = useRef(false);
  const interval = useRef<number | undefined>();
  useFirstRenderEffect(() => {
    if (scrollToTx) {
      setTimeout(() => scroller.scrollTo(scrollToTx, { containerId: mainContainerId }), 100);
    }
  });
  useFirstRenderEffect(() => {
    const refreshData = async (): Promise<void> => {
      if (filterVisible.current) {
        return;
      }
      if (!hideBalance) {
        reloadBalance({})(dispatch);
      }
      if (!loading.current) {
        loading.current = true;
        try {
          await reloadTransactions({})(dispatch);
        } finally {
          loading.current = false;
        }
      }
    };
    refreshData();
    interval.current = setInterval(refreshData, 3000);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  });
  const transactions = useSelector(makeSelectTransactions());
  const balance = useSelector(selectBalance);
  const isMobile = useSelector(selectIsMobile);
  const isFrame = useSelector(selectIsFrame);

  const loadMoreData = useCallback(async () => {
    if (loading.current) {
      return;
    }
    try {
      loading.current = true;
      await requestTransactions({})(dispatch);
    } finally {
      loading.current = false;
    }
  }, [dispatch]);

  // precondition - data is already sorted array by timestamp desc
  const dataToRender =
    transactions?.data.reduce((result, tx) => {
      const previous = result[result.length - 1];
      let date;
      if (previous?.tx) {
        const previousDay = moment(previous.tx.timestamp).startOf('d');
        const day = moment(tx.timestamp).startOf('d');
        date = day.isBefore(previousDay) ? day.toDate() : undefined;
      } else {
        date = moment(tx.timestamp)
          .startOf('d')
          .toDate();
      }
      return [...result, ...(date ? [{ date }] : []), { tx }];
    }, asType<DataToRender[]>([])) || [];

  return (
    <MainLayout
      data-test={dataTest && `${dataTest}-layout`}
      containerId={mainContainerId}
      minHeight={200}
      height={800}
      buttons={{
        back,
        logout: !isFrame ? <FormattedMessage id="demo.logout.button.title" /> : undefined,
      }}
      header={{
        className: isMobile ? classes.headerMobile : classes.header,
        postfix: (
          <Space direction="vertical">
            {!hideBalance && (
              <WalletValue
                data-test={dataTest && `${dataTest}-wallet`}
                hideTitle
                value={balance}
                options={
                  withRecharge
                    ? [
                      <PrimaryButton
                        data-test={dataTest && `${dataTest}-recharge`}
                        key="recharge"
                        startIcon={<RechargeIcon />}
                        title={<FormattedMessage id="demo.pages.transactions.options.recharge" />}
                      />,
                      ]
                    : undefined
                }
              />
            )}
            <TransactionFilter
              data-test={dataTest && `${dataTest}-filter`}
              value={filter}
              onChange={updateFilter}
              onFilterVisibilityChanged={setFilterVisible}
            />
          </Space>
        ),
      }}
    >
      {transactions ? (
        <InfiniteScroll
          dataLength={transactions.data.length}
          next={loadMoreData}
          hasMore={transactions.hasMore}
          scrollableTarget={mainContainerId}
          loader={undefined}
        >
          {dataToRender.map((data) => {
            if (data?.date) {
              return (
                <TransactionsDate
                  data-test={dataTest && `${dataTest}-date-${data.date.toDateString()}`}
                  key={data.date.toDateString()}
                  style={{}}
                  value={data.date}
                />
              );
            }
            if (data?.tx) {
              return (
                <TransactionRow
                  data-test={dataTest && `${dataTest}-row-${data.tx.transactionId}`}
                  key={`${data.tx.transactionId || data.tx.cid}`}
                  value={data.tx}
                  style={{}}
                  basePath={basePath}
                />
              );
            }
            return <>&nbsp;</>;
          })}
        </InfiniteScroll>
      ) : (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}
          data-test={dataTest && `${dataTest}-loading`}
        >
          <CircularProgress color="inherit" />
        </div>
      )}
    </MainLayout>
  );
};

export default TransactionsPageView;
